/**
 * tokenHtml -- client-side token chip + resolver helpers for the email
 * template editor (Phase 1 PR-4).
 *
 * LOAD-BEARING FORMAT CONTRACT. The persisted token-keyed HTML MUST match
 * what the backend engine (``api/src/emails/tokens.py``) resolves and what
 * the PR-2 storage endpoint round-trips:
 *
 *   - Chip form (what the contenteditable editor emits):
 *       <span class="tok" contenteditable="false" data-key="KEY">Label</span>
 *     The sanitizer allowlists ``class`` + ``data-key`` on ``span`` so this
 *     survives sanitize-on-write. (``contenteditable`` is stripped by the
 *     sanitizer; it is re-applied by the editor when the stored HTML is
 *     re-loaded, so round-trip is preserved.)
 *   - Text form: bare ``{token}`` in a subject/body -- also resolved by the
 *     backend engine.
 *
 * The token LABEL/SAMPLE data is NOT hardcoded here -- it is passed in from
 * the backend ``_catalog`` endpoint (the single source of truth), so the UI
 * never offers a token the send pipeline can't resolve.
 */

/**
 * Build the chip HTML the editor inserts for a token.
 * @param {string} key   token key (e.g. "first_name")
 * @param {string} label friendly label (e.g. "First name") from the catalog
 */
export function chipHtml(key, label) {
  const safeKey = String(key).replace(/[^a-z0-9_]/gi, "");
  const safeLabel = escapeHtml(label == null ? key : label);
  return `<span class="tok" contenteditable="false" data-key="${safeKey}">${safeLabel}</span>`;
}

/** Minimal HTML-escape for splicing a label into chip markup. */
export function escapeHtml(value) {
  return String(value == null ? "" : value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Resolve chip spans + bare ``{token}`` text -> sample values, for the
 * preview BODY (an HTML string). Mirrors the design's ``resolveHTML``.
 *
 * ESCAPING MIRRORS THE BACKEND, WHICH IT PREVIOUSLY DID NOT. The backend's
 * ``_resolve_value`` HTML-escapes every token value on splice; this function
 * resolved chips to a TEXT NODE (safe) but then handed the serialized HTML
 * to ``resolveTextTokens``, which spliced a bare ``{token}``'s value as RAW
 * HTML and let the browser re-parse it. A catalog sample containing
 * ``<img src=x onerror=...>`` therefore rendered a live element in the
 * preview pane. Samples are backend-supplied so this was not reachable by a
 * distributor, but it was one careless sample away from being an editor-side
 * injection, and it made the preview disagree with the send path about what
 * a token value is. Values are now escaped here too.
 *
 * ``htmlOf`` is the single, narrow exception: a token whose catalog entry
 * carries ``sample_html`` (today only ``order_link``, which the backend
 * renders as an anchor) is spliced as markup so the preview shows the link
 * the customer will receive rather than visible ``<a href=...>``
 * characters. It is consulted ONLY for that field, which is
 * backend-generated and never distributor input.
 *
 * @param {string} html
 * @param {(key:string)=>string|undefined} sampleOf   plain sample value
 * @param {(key:string)=>string|undefined} [htmlOf]   pre-rendered markup
 */
export function resolveBodyHtml(html, sampleOf, htmlOf) {
  if (!html) return "";
  const box = document.createElement("div");
  box.innerHTML = html;
  box.querySelectorAll("[data-key]").forEach((el) => {
    const key = el.getAttribute("data-key");
    const markup = htmlOf && htmlOf(key);
    if (markup) {
      // Parse the backend-supplied markup and splice the NODES, so the
      // anchor survives the innerHTML round-trip below as an element
      // rather than as text that would then be escaped.
      const frag = document.createElement("div");
      frag.innerHTML = markup;
      el.replaceWith(...frag.childNodes);
      return;
    }
    const sample = sampleOf(key);
    el.replaceWith(document.createTextNode(sample == null ? "" : sample));
  });
  // Also resolve any bare {token} text left in the body.
  return resolveTextTokens(box.innerHTML, sampleOf, htmlOf);
}

/**
 * Resolve bare ``{token}`` placeholders in a string to sample values
 * (preview SUBJECT + any {token} text left in a body). Unknown tokens stay
 * literal (matching the backend's leave-unknown-literal behavior).
 *
 * The result is spliced into an HTML string, so a resolved value is
 * HTML-ESCAPED -- see ``resolveBodyHtml`` for why, and why ``htmlOf`` is
 * the one exception. Pass no ``htmlOf`` for a plain-text destination such
 * as the subject line, where markup is never wanted.
 */
export function resolveTextTokens(str, sampleOf, htmlOf) {
  if (!str) return "";
  return String(str).replace(/\{(\w+)\}/g, (whole, key) => {
    const markup = htmlOf && htmlOf(key);
    if (markup) return markup;
    const sample = sampleOf(key);
    return sample == null ? whole : escapeHtml(sample);
  });
}

/**
 * Resolve a subject's chip HTML down to plain sample text (strip tags,
 * resolve chips + {token} text). Used for the preview subject line.
 *
 * Deliberately does NOT forward an ``htmlOf``: a subject line is plain text
 * in every mail client, and the backend resolves it as text for exactly
 * that reason (``resolve_email_copy`` always renders the subject in TEXT
 * form). Rendering an anchor here and flattening it back to text would
 * still agree, but not forwarding it keeps the intent explicit -- a subject
 * never carries markup.
 */
export function resolveSubjectText(html, sampleOf) {
  const box = document.createElement("div");
  box.innerHTML = resolveBodyHtml(html, sampleOf);
  return box.textContent || "";
}

/**
 * Turn a token-keyed ``{token}`` TEMPLATE string (e.g. a default subject
 * from the catalog) into chip HTML for seeding the editor.
 * @param {string} str
 * @param {(key:string)=>string|undefined} labelOf key -> friendly label
 */
export function templateToChips(str, labelOf) {
  if (!str) return "";
  return String(str).replace(/\{(\w+)\}/g, (whole, key) => {
    const label = labelOf(key);
    return label == null ? whole : chipHtml(key, label);
  });
}
